import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { Component, ContentChild, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { debounceTime, Subject } from 'rxjs';
import { IColumnConfigTableResponsive } from 'src/app/domain/IColumnConfigTableResponsive';
import { FieldTypeEnum } from 'src/app/domain/registration/enums/field-type.enum';
import { TypeIndexEnum } from 'src/app/domain/registration/enums/type-index.enum';
import { KeyboardService } from 'src/app/shared/components/keyboard/keyboard.service';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { S3_URL_BASE } from 'src/environments/environment';

@Component({
  selector: 'app-table-extensible',
  templateUrl: './table-extensible.component.html',
  styleUrls: ['./table-extensible.component.sass'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class TableExtensibleComponent implements OnInit {

  @ContentChild(TemplateRef)
  templateRef : TemplateRef<any>;

  // columnsToDisplayWithExpand = [...this.columnsToDisplay, 'expand'];
  expandedElement: PeriodicElement | null;

  constructor(public keyboardService: KeyboardService) {}

  @Input() hasFilter: boolean = true;
  @Input() lockProperty: string = '';
  @Input() activeProperty: string = '';
  @Input() lockPeopleProperty: string = '';
  @Input() showLock: boolean = false;
  @Input() showActive: boolean = false;
  @Input() showPeopleLock: boolean = false;
  @Input() showKey: boolean = false;
  @Input() hasAdd: boolean = true;
  @Input() type: TypeIndexEnum = TypeIndexEnum.CRUD;
  @Input() length: number = 0;
  @Output() changePage: EventEmitter<any> = new EventEmitter<any>();
  @Output() filter: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSelectionChange: EventEmitter<any> = new EventEmitter<any>();

  keyboardChange: EventEmitter<any> = new EventEmitter<any>();
  filterValue: string = '';

  pageSize = 10;
  pageSizeOptions: number[] = [5, 10, 25, 100];
  selection = new SelectionModel<any>(true, []);

  ngOnInit(): void {
    this.keyboardChange.subscribe((event) => {
      this.applyFilter(event);
    });

    this.filterSubject.pipe(debounceTime(500)).subscribe((value) => {
      this.filter.emit(value?.toUpperCase());
    });
  }

  public pageEvent(event: PageEvent): PageEvent {
    this.changePage.emit({ page: event.pageIndex, limit: event.pageSize });
    // this.lowValue = event.pageIndex * event.pageSize;
    // this.highValue = this.lowValue + event.pageSize;
    return event;
  }

  setPageSizeOptions(setPageSizeOptionsInput: string) {
    if (setPageSizeOptionsInput) {
      this.pageSizeOptions = setPageSizeOptionsInput
        .split(',')
        .map((str) => +str);
    }
  }

  @ViewChild(MatPaginator)
  paginator!: MatPaginator;
  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  @Input('hideFilter') hideFilter: boolean;
  @Input('hidePagination') hidePagination: boolean;
  @Input('displayedColumns')
  displayedColumns: string[];
  @Input('displayedColumnsOnDetail')
  displayedColumnsOnDetail: string[];
  @Input('dataSource')
  dataSource: MatTableDataSource<any>;
  @Input('configColumns')
  configColumns: IColumnConfigTableResponsive[];

  @Output() openShow: EventEmitter<any> = new EventEmitter();
  @Output() openNew: EventEmitter<any> = new EventEmitter();
  @Output() openEdit: EventEmitter<any> = new EventEmitter();
  @Output() openDelete: EventEmitter<any> = new EventEmitter();
  @Output() lock: EventEmitter<any> = new EventEmitter();
  @Output() unlock: EventEmitter<any> = new EventEmitter();
  @Output() active: EventEmitter<any> = new EventEmitter();
  @Output() inactive: EventEmitter<any> = new EventEmitter();
  @Output() lockPeople: EventEmitter<any> = new EventEmitter();
  @Output() unlockPeople: EventEmitter<any> = new EventEmitter();
  @Output() key: EventEmitter<any> = new EventEmitter();

  private filterSubject = new Subject<string>();

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.selection.changed.subscribe((items) => {
      this.onSelectionChange.emit(items);
    });
  }

  applyFilter(value: string) {
    this.filterSubject.next(value);
  }

  get typeIndexEnum() {
    return TypeIndexEnum;
  }

  get fieldTypeEnum() {
    return FieldTypeEnum;
  }

  public getUrl(value: string) {
    return `${S3_URL_BASE}${value}`;
  }

  showRow(element: any, row: any) {
    element.idx = row;
    this.openShow.emit(element);
  }

  editRow(element: any, row: any) {
    element.idx = row;
    this.openEdit.emit(element);
  }

  deleteRow(element: any, row: any) {
    element.idx = row;
    this.openDelete.emit(element);
  }

  getValue(config: IColumnConfigTableResponsive, data: any) {
    return GlobalUtils.getValueByTypeColumnTableResponsive(config, data);
  }

  // Checkbox
  select(element: any, row: any) {
    element.idx = row;
    this.onSelect.emit(element);
  }

  public onCheckRow($event: MouseEvent, idx?: number) {
    if ($event) $event.stopPropagation();
    this.onSelect.emit($event);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
      row.saleNumber + 1
    }`;
  }

  hasSelectConfig() {
    return this.displayedColumns.some((col) => col === 'select');
  }

  activeShowLockClass(element: any) {
    if (this.showLock) return element[this.activeProperty] ? 'active' : 'inactive';

    return 'active';
  }

}

export interface PeriodicElement {
  name: string;
  position: number;
  weight: number;
  symbol: string;
  description: string;
}

const ELEMENT_DATA: PeriodicElement[] = [
  {
    position: 1,
    name: 'Hydrogen',
    weight: 1.0079,
    symbol: 'H',
    description: `Hydrogen is a chemical element with symbol H and atomic number 1. With a standard
        atomic weight of 1.008, hydrogen is the lightest element on the periodic table.`,
  },
  {
    position: 2,
    name: 'Helium',
    weight: 4.0026,
    symbol: 'He',
    description: `Helium is a chemical element with symbol He and atomic number 2. It is a
        colorless, odorless, tasteless, non-toxic, inert, monatomic gas, the first in the noble gas
        group in the periodic table. Its boiling point is the lowest among all the elements.`,
  },
  {
    position: 3,
    name: 'Lithium',
    weight: 6.941,
    symbol: 'Li',
    description: `Lithium is a chemical element with symbol Li and atomic number 3. It is a soft,
        silvery-white alkali metal. Under standard conditions, it is the lightest metal and the
        lightest solid element.`,
  },
  {
    position: 4,
    name: 'Beryllium',
    weight: 9.0122,
    symbol: 'Be',
    description: `Beryllium is a chemical element with symbol Be and atomic number 4. It is a
        relatively rare element in the universe, usually occurring as a product of the spallation of
        larger atomic nuclei that have collided with cosmic rays.`,
  },
  {
    position: 5,
    name: 'Boron',
    weight: 10.811,
    symbol: 'B',
    description: `Boron is a chemical element with symbol B and atomic number 5. Produced entirely
        by cosmic ray spallation and supernovae and not by stellar nucleosynthesis, it is a
        low-abundance element in the Solar system and in the Earth's crust.`,
  },
  {
    position: 6,
    name: 'Carbon',
    weight: 12.0107,
    symbol: 'C',
    description: `Carbon is a chemical element with symbol C and atomic number 6. It is nonmetallic
        and tetravalent—making four electrons available to form covalent chemical bonds. It belongs
        to group 14 of the periodic table.`,
  },
  {
    position: 7,
    name: 'Nitrogen',
    weight: 14.0067,
    symbol: 'N',
    description: `Nitrogen is a chemical element with symbol N and atomic number 7. It was first
        discovered and isolated by Scottish physician Daniel Rutherford in 1772.`,
  },
  {
    position: 8,
    name: 'Oxygen',
    weight: 15.9994,
    symbol: 'O',
    description: `Oxygen is a chemical element with symbol O and atomic number 8. It is a member of
         the chalcogen group on the periodic table, a highly reactive nonmetal, and an oxidizing
         agent that readily forms oxides with most elements as well as with other compounds.`,
  },
  {
    position: 9,
    name: 'Fluorine',
    weight: 18.9984,
    symbol: 'F',
    description: `Fluorine is a chemical element with symbol F and atomic number 9. It is the
        lightest halogen and exists as a highly toxic pale yellow diatomic gas at standard
        conditions.`,
  },
  {
    position: 10,
    name: 'Neon',
    weight: 20.1797,
    symbol: 'Ne',
    description: `Neon is a chemical element with symbol Ne and atomic number 10. It is a noble gas.
        Neon is a colorless, odorless, inert monatomic gas under standard conditions, with about
        two-thirds the density of air.`,
  },
];
